import React, { useEffect, useRef } from "react";
import AutoResizeTextarea from "../AutoResizeTextarea";

const PanelTresorerie = ({
  indicators,
  isDirty,
  setIsDirty,
  editableResult,
  setEditableResult,
}) => {
  const waterfallRef = useRef(null);
  const waterfallInstance = useRef(null);

  // ── Extraction des données ──
  const client = indicators?.client || {};
  const bilan = indicators?.bilan || {};

  const exerciceN = client.exercice_N || "N";
  const exerciceN1 = client.exercice_N1 || "N-1";

  const tresorerieN = bilan.tresorerie_N || 0;
  const tresorerieN1 = bilan.tresorerie_N1 || 0;
  const tresorerieVarPct = bilan.tresorerie_var_pct || 0;
  const dettesFournisseursN = bilan.dettes_fournisseurs_N || 0;
  const dettesFournisseursN1 = bilan.dettes_fournisseurs_N1 || 0;
  const cafN = bilan.caf_N || 0;
  const cafVarPct = bilan.caf_var_pct || 0;

  const fmt = (v, d = 0) =>
    Number(v).toLocaleString("fr-FR", {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
  const fmtE = (v) => fmt(v) + " €";
  const fmtPct = (v) => (v >= 0 ? "+" : "") + fmt(v, 1) + "%";
  const deltaClass = (v) => (v >= 0 ? "pos" : "neg");
  const deltaArrow = (v) => (v >= 0 ? "▲" : "▼");

  const cafKpi = cafN >= 0 ? "kpi-green" : "kpi-red";
  const tresoKpi = tresorerieN >= 0 ? "kpi-teal" : "kpi-red";

  // ── Données waterfall ──
  const wfSoldeInitial = tresorerieN1;
  const wfCAF = cafN;
  const wfDettesVar = dettesFournisseursN - dettesFournisseursN1;
  const wfSoldeFinal = tresorerieN;

  // ── Chargement Chart.js + init du waterfall ──
  useEffect(() => {
    let cancelled = false;

    const ensureChartJs = () =>
      new Promise((resolve, reject) => {
        if (window.Chart) return resolve(window.Chart);
        const existing = document.getElementById("chartjs-cdn");
        if (existing) {
          existing.addEventListener("load", () => resolve(window.Chart));
          existing.addEventListener("error", reject);
          return;
        }
        const s = document.createElement("script");
        s.id = "chartjs-cdn";
        s.src =
          "https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js";
        s.async = true;
        s.onload = () => resolve(window.Chart);
        s.onerror = reject;
        document.head.appendChild(s);
      });

    ensureChartJs().then((Chart) => {
      if (cancelled || !Chart || !waterfallRef.current) return;

      Chart.defaults.font.family = "'DM Sans', system-ui, sans-serif";
      Chart.defaults.font.size = 11;
      Chart.defaults.color = "#8A849A";

      const GL = "#E6E3EC";
      const GR = "#16A34A";
      const RD = "#DC2626";

      // Nettoyage instance précédente
      if (waterfallInstance.current) {
        waterfallInstance.current.destroy();
      }

      const moves = [wfCAF, wfDettesVar];
      let acc = wfSoldeInitial;
      const floats = [[0, wfSoldeInitial]];
      const bgs = ["rgba(8,145,178,.2)"];
      const borders = ["#0891B2"];

      moves.forEach((m) => {
        if (m >= 0) {
          floats.push([acc, acc + m]);
          bgs.push("rgba(22,163,74,.2)");
          borders.push(GR);
        } else {
          floats.push([acc + m, acc]);
          bgs.push("rgba(220,38,38,.15)");
          borders.push(RD);
        }
        acc += m;
      });

      floats.push([0, wfSoldeFinal]);
      bgs.push(
        wfSoldeFinal >= 0 ? "rgba(8,145,178,.2)" : "rgba(220,38,38,.15)"
      );
      borders.push(wfSoldeFinal >= 0 ? "#0891B2" : RD);

      waterfallInstance.current = new Chart(waterfallRef.current, {
        type: "bar",
        data: {
          labels: [
            `Tréso ${exerciceN1}`,
            `CAF ${exerciceN}`,
            "Var. dettes fourn.",
            `Tréso ${exerciceN}`,
          ],
          datasets: [
            {
              data: floats,
              backgroundColor: bgs,
              borderColor: borders,
              borderWidth: 1.5,
              borderSkipped: false,
              borderRadius: 4,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const [a, b] = ctx.raw;
                  return (
                    (b - a >= 0 ? "+" : "") +
                    (b - a).toLocaleString("fr-FR") +
                    " €"
                  );
                },
              },
            },
          },
          scales: {
            x: {
              grid: { color: GL },
              border: { display: false },
              ticks: {
                callback: (v) => v.toLocaleString("fr-FR") + " €",
              },
            },
            y: {
              grid: { display: false },
              border: { display: false },
            },
          },
        },
      });
    });

    return () => {
      cancelled = true;
      if (waterfallInstance.current) {
        waterfallInstance.current.destroy();
        waterfallInstance.current = null;
      }
    };
  }, [wfSoldeInitial, wfCAF, wfDettesVar, wfSoldeFinal, exerciceN, exerciceN1]);

  const dettesVar = dettesFournisseursN - dettesFournisseursN1;

  return (
    <div className="treso-wrapper">
      <style>{`
        .treso-wrapper {
          --brand:#7E1738; --brand-mid:#9B1F44;
          --bg:#F5F3F7;
          --border:#E6E3EC; --border-light:#F0EDF4;
          --text:#1A1525; --text-2:#4A4460; --muted:#8A849A;
          --green:#16A34A; --green-bg:#F0FDF4; --green-mid:#DCFCE7;
          --amber:#D97706; --amber-bg:#FFFBEB; --amber-mid:#FEF3C7;
          --red:#DC2626; --red-bg:#FEF2F2; --red-mid:#FEE2E2;
          --teal:#0891B2; --teal-bg:#ECFEFF; --teal-mid:#CFFAFE;
          --indigo:#4F46E5; --indigo-bg:#EEF2FF; --indigo-mid:#C7D2FE;
          --radius:10px;
          font-family:'DM Sans',system-ui,sans-serif;
          color:var(--text);
          font-size:13px;
          padding:20px 28px 32px;
          background:var(--bg);
          display:flex;
          flex-direction:column;
          gap:16px;
        }

        /* KPI GRID */
        .tr-kpi-grid{
          display:grid;
          grid-template-columns:repeat(4,1fr);
          gap:14px;
        }
        .tr-kpi-card{
          background:var(--kpi-bg,var(--teal-bg));
          border:1px solid var(--kpi-mid,var(--teal-mid));
          border-radius:var(--radius);
          padding:16px 18px;
          display:flex;
          flex-direction:column;
          gap:3px;
          transition:transform .15s,box-shadow .15s;
        }
        .tr-kpi-card:hover{
          transform:translateY(-1px);
          box-shadow:0 4px 16px rgba(0,0,0,.07);
        }
        .tr-kpi-number{
          font-size:22px;
          font-weight:700;
          color:var(--kpi-color,var(--teal));
          line-height:1.1;
          letter-spacing:-.02em;
        }
        .tr-kpi-lbl{
          font-size:11px;
          font-weight:500;
          color:var(--kpi-color,var(--teal));
          opacity:.75;
          margin-top:1px;
        }
        .tr-kpi-delta{
          display:inline-flex;
          align-items:center;
          font-size:10.5px;
          font-weight:500;
          padding:2px 8px;
          border-radius:20px;
          margin-top:5px;
          width:fit-content;
        }
        .tr-kpi-delta.neg{background:rgba(220,38,38,.1);color:#B91C1C}
        .tr-kpi-delta.pos{background:rgba(22,163,74,.1);color:#15803D}
        .tr-kpi-delta.neu{background:rgba(100,100,120,.08);color:var(--text-2)}
        .kpi-teal{--kpi-bg:var(--teal-bg);--kpi-mid:var(--teal-mid);--kpi-color:var(--teal)}
        .kpi-green{--kpi-bg:var(--green-bg);--kpi-mid:var(--green-mid);--kpi-color:var(--green)}
        .kpi-red{--kpi-bg:var(--red-bg);--kpi-mid:var(--red-mid);--kpi-color:var(--red)}
        .kpi-amber{--kpi-bg:var(--amber-bg);--kpi-mid:var(--amber-mid);--kpi-color:var(--amber)}

        /* Layout principal */
        .tr-main{
          display:grid;
          grid-template-columns:3fr 2fr;
          gap:14px;
          align-items:stretch;
        }

        /* Cards */
        .tr-card{
          background:#fff;
          border:1px solid var(--border);
          border-radius:var(--radius);
          padding:18px 20px;
        }
        .tr-card-head{
          display:flex;
          align-items:center;
          justify-content:space-between;
          margin-bottom:14px;
        }
        .tr-card-title{
          font-size:12.5px;
          font-weight:600;
          color:var(--text);
        }
        .tr-card-sub{
          font-size:11px;
          color:var(--muted);
        }
        .tr-sec-label{
          font-size:10px;
          font-weight:700;
          letter-spacing:.14em;
          text-transform:uppercase;
          color:var(--muted);
          padding:4px 0 10px;
          border-bottom:1px solid var(--border);
          margin-bottom:14px;
        }

        /* Chart */
        .tr-chart-card{
          display:flex;
          flex-direction:column;
        }
        .tr-chart-box{
          position:relative;
          flex:1;
          min-height:260px;
        }
        .tr-chart-box canvas{
          width:100%!important;
          display:block;
        }

        /* Right column */
        .tr-right{
          display:flex;
          flex-direction:column;
          gap:14px;
        }

        /* Table */
        .tr-card table{
          width:100%;
          border-collapse:collapse;
          font-size:12.5px;
        }
        .tr-card td{
          padding:10px 12px;
          color:var(--text-2);
          border-bottom:1px solid var(--border-light);
          vertical-align:middle;
        }
        .tr-card td:last-child{
          text-align:right;
          font-variant-numeric:tabular-nums;
          font-weight:600;
        }
        .tr-card tbody tr:last-child td{border-bottom:none}
        .tr-card .tr-tot td{
          background:var(--brand)!important;
          color:rgba(255,255,255,.95)!important;
          font-weight:600;
        }
        .v-pos{color:var(--green)!important;font-weight:600}
        .v-neg{color:var(--red)!important;font-weight:600}
        .tr-card .tr-tot .v-pos,
        .tr-card .tr-tot .v-neg{
          color:rgba(255,255,255,.95)!important;
        }

        /* Tiles */
        .tr-tile-analyse{
          background:var(--indigo-bg);
          border:1px solid var(--indigo-mid);
          border-left:3px solid var(--indigo);
          border-radius:var(--radius);
          padding:16px 18px;
        }
        .tr-tile-analyse-head{
          display:flex;
          align-items:center;
          gap:8px;
          margin-bottom:10px;
        }
        .tr-tile-analyse-lbl{
          font-size:10px;
          font-weight:700;
          letter-spacing:.14em;
          text-transform:uppercase;
          color:var(--indigo);
        }
        .tr-tile-analyse p{
          font-size:12.5px;
          line-height:1.8;
          color:#3730A3;
        }
        .tr-tile-infos{
          background:#FDF0F3;
          border:1px solid #F2D0DA;
          border-left:3px solid var(--brand);
          border-radius:var(--radius);
          padding:16px 18px;
        }
        .tr-tile-infos-head{
          font-size:10px;
          font-weight:700;
          letter-spacing:.14em;
          text-transform:uppercase;
          color:var(--brand);
          margin-bottom:10px;
        }
        .tr-tile-infos p{
          font-size:12.5px;
          line-height:1.8;
          color:var(--text-2);
        }
      `}</style>

      {/* ── KPI GRID ── */}
      <div className="tr-kpi-grid">
        <div className="tr-kpi-card kpi-teal">
          <div className="tr-kpi-number">{fmtE(tresorerieN1)}</div>
          <div className="tr-kpi-lbl">Trésorerie début {exerciceN}</div>
          <span className="tr-kpi-delta neu">Solde {exerciceN1}</span>
        </div>

        <div className={`tr-kpi-card ${cafKpi}`}>
          <div className="tr-kpi-number">{fmtE(cafN)}</div>
          <div className="tr-kpi-lbl">CAF {exerciceN}</div>
          <span className={`tr-kpi-delta ${deltaClass(cafVarPct)}`}>
            {deltaArrow(cafVarPct)} {fmtPct(cafVarPct)} vs {exerciceN1}
          </span>
        </div>

        <div className="tr-kpi-card kpi-amber">
          <div className="tr-kpi-number">{fmtE(dettesFournisseursN)}</div>
          <div className="tr-kpi-lbl">Dettes fournisseurs</div>
          <span className="tr-kpi-delta neu">
            {exerciceN1} : {fmtE(dettesFournisseursN1)}
          </span>
        </div>

        <div className={`tr-kpi-card ${tresoKpi}`}>
          <div className="tr-kpi-number">{fmtE(tresorerieN)}</div>
          <div className="tr-kpi-lbl">Trésorerie fin {exerciceN}</div>
          <span className={`tr-kpi-delta ${deltaClass(tresorerieVarPct)}`}>
            {deltaArrow(tresorerieVarPct)} {fmtPct(tresorerieVarPct)}
          </span>
        </div>
      </div>

      {/* ── LAYOUT PRINCIPAL ── */}
      <div className="tr-main">
        {/* Chart waterfall */}
        <div className="tr-card tr-chart-card">
          <div className="tr-card-head">
            <div className="tr-card-title">Flux de trésorerie {exerciceN}</div>
            <span className="tr-card-sub">Waterfall</span>
          </div>
          <div className="tr-chart-box">
            <canvas ref={waterfallRef} />
          </div>
        </div>

        {/* Colonne droite */}
        <div className="tr-right">
          <div className="tr-card">
            <div className="tr-sec-label">Détail des flux</div>
            <table>
              <tbody>
                <tr>
                  <td>Trésorerie {exerciceN1}</td>
                  <td className="v-pos">{fmtE(tresorerieN1)}</td>
                </tr>
                <tr>
                  <td>CAF</td>
                  <td className={cafN >= 0 ? "v-pos" : "v-neg"}>
                    {fmtE(cafN)}
                  </td>
                </tr>
                <tr>
                  <td>Variation dettes fourn.</td>
                  <td className={dettesVar >= 0 ? "v-pos" : "v-neg"}>
                    {fmtE(dettesVar)}
                  </td>
                </tr>
                <tr className="tr-tot">
                  <td>Trésorerie {exerciceN}</td>
                  <td>{fmtE(tresorerieN)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            className="rounded-md border-l-[3px] p-4"
            style={{ backgroundColor: "#EEF2FF", borderLeftColor: "#4F46E5" }}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                Analyse
              </p>

              {isDirty.analyseTresorerie && (
                <span className="bg-amber-100 text-amber-700 border border-amber-200 text-[10px] px-2 py-0.5 rounded-md shadow-sm">
                  Modifié
                </span>
              )}
            </div>

            <AutoResizeTextarea
              value={editableResult?.tresorerie?.analyse || ""}
              onChange={(e) => {
                setIsDirty((prev) => ({
                  ...prev,
                  analyseTresorerie: true,
                }));
                setEditableResult((prev) => ({
                  ...prev,
                  tresorerie: {
                    ...prev.tresorerie,
                    analyse: e.target.value,
                  },
                }));
              }}
              className="text-indigo-900/80"
            />
          </div>

          <div
            className="rounded-md border-l-[3px] p-4"
            style={{ backgroundColor: "#FDF0F3", borderLeftColor: "#7E1738" }}
          >
            <div className="flex items-center justify-between mb-2">
              <p
                className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: "#7E1738" }}
              >
                Informations & Recommandations
              </p>

              {isDirty.informations_recommandationsTresorerie && (
                <span className="bg-amber-100 text-amber-700 border border-amber-200 text-[10px] px-2 py-0.5 rounded-md shadow-sm">
                  Modifié
                </span>
              )}
            </div>

            <AutoResizeTextarea
              value={
                editableResult?.tresorerie?.informations_recommandations || ""
              }
              onChange={(e) => {
                setIsDirty((prev) => ({
                  ...prev,
                  informations_recommandationsTresorerie: true,
                }));
                setEditableResult((prev) => ({
                  ...prev,
                  tresorerie: {
                    ...prev.tresorerie,
                    informations_recommandations: e.target.value,
                  },
                }));
              }}
              className="text-foreground/80"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelTresorerie;
